/**
 * Analytics API client (shapes from `@velora/contracts/analytics`). Office
 * scoped calls take the client the shell pins to the office selector, so a
 * cached response always belongs to the selection it was keyed on.
 *
 * Query keys start with an entity key ({ live: [...] } carries the others),
 * so the shell refetches them when an appointment, procedure or ledger row
 * changes anywhere in scope.
 */
import {
  AgingReportSchema,
  BoardSchema,
  DaySheetSchema,
  FeeOutliersSchema,
  FinderResultSchema,
  FinderTasksResultSchema,
  BelowContractSchema,
  BelowContractTasksResultSchema,
  GoalsSchema,
  GoalSuggestionsSchema,
  RenegotiationPackSchema,
  GoalsV2Schema,
  KpiScorecardSchema,
  type KpiScorecardQuery,
  ReportSubscriptionsSchema,
  ScheduleAnalysisSchema,
  TaskResultsSchema,
  type ReportSubscriptions,
  UnscheduledAgingSchema,
  type AnalyticsSettingPut,
  type GoalTargetPut,
  HuddleSchema,
  HuddleSummarySchema,
  MetricDictionarySchema,
  MetricRowsSchema,
  OfficeComparisonSchema,
  OfficeGoalSchema,
  OutlierAckResultSchema,
  ProductionReportSchema,
  ReferralReportSchema,
  ScorecardsSchema,
  PivotResultSchema,
  MetricSeriesSchema,
  RecoverableMoneySchema,
  FinderSearchResultSchema,
  FinderSearchExportSchema,
  FinderActionResultSchema,
  FinderFacetsSchema,
  FinderSavedSearchesSchema,
  FinderSavedSearchSchema,
  type FinderActionBody,
  type FinderFilters,
  type FinderSearchBody,
  type PivotQuery,
  type MetricSeriesQuery,
  type BoardQuery,
  type CompareQuery,
  type FinderQuery,
  type FinderTasksBody,
  type GoalPut,
  type HuddleQuery,
  type MetricPresetKey,
  type MetricRowsQuery,
  type ProductionReportQuery,
} from '@velora/contracts/analytics';
import type { AlertThresholdsPut } from '@velora/contracts/owner-insights';
import { MoneyBoardSchema } from '@velora/contracts/collections-ladder';
import { buildPath, type HttpClient } from '@velora/sdk';
import { snapshotRequest } from '../../app/snapshot.js';
import { z } from 'zod';

const SavedSchema = z.object({ saved: z.number().int() });

type Query = Record<string, string | number | boolean | undefined>;

const opt = (signal?: AbortSignal) => (signal ? { signal } : {});

/** Live entities every analytics view depends on. */
export const LIVE = { live: ['appointment', 'procedure', 'ledger_entry', 'patient'] } as const;

export const analyticsKeys = {
  board: (q: BoardQuery) => ['analytics', 'board', q, LIVE] as const,
  money: () => ['analytics', 'money', LIVE] as const,
  compare: (q: CompareQuery) => ['analytics', 'compare', q, LIVE] as const,
  rows: (id: string, q: MetricRowsQuery) => ['analytics', 'rows', id, q, LIVE] as const,
  huddle: (q: HuddleQuery) => ['analytics', 'huddle', q, LIVE] as const,
  huddleSummary: (q: HuddleQuery) => ['analytics', 'huddle-summary', q, LIVE] as const,
  providers: (q: Query) => ['analytics', 'providers', q, LIVE] as const,
  finder: (q: FinderQuery) => ['analytics', 'finder', q, LIVE] as const,
  daySheet: (date: string) => ['analytics', 'day-sheet', date, LIVE] as const,
  production: (q: ProductionReportQuery) => ['analytics', 'production', q, LIVE] as const,
  aging: (asOf: string, page = 1) => ['analytics', 'aging', asOf, page, LIVE] as const,
  referrals: (from: string, to: string) => ['analytics', 'referrals', from, to, LIVE] as const,
  outliers: () => ['analytics', 'outliers', LIVE] as const,
  goals: (month: string) => ['analytics', 'goals', month] as const,
  scorecard: (q: KpiScorecardQuery) => ['analytics', 'scorecard', q, LIVE] as const,
  taskResults: (from: string, to: string) => ['analytics', 'task-results', from, to, LIVE] as const,
  reportSubs: () => ['analytics', 'report-subscriptions'] as const,
  goalsV2: (month: string) => ['analytics', 'goals-v2', month, LIVE] as const,
  schedule: (month: string) => ['analytics', 'schedule-analysis', month, LIVE] as const,
  unscheduledAging: (asOf: string) => ['analytics', 'unscheduled-aging', asOf, LIVE] as const,
  dictionary: (preset: MetricPresetKey) => ['analytics', 'dictionary', preset] as const,
  pivot: (q: PivotQuery) => ['analytics', 'pivot', q, LIVE] as const,
  finderSearch: (b: FinderSearchBody) => ['analytics', 'finder-search', b, LIVE] as const,
  finderFacets: () => ['analytics', 'finder-facets'] as const,
  finderSaved: () => ['analytics', 'finder-saved'] as const,
  recoverable: () => ['analytics', 'recoverable', LIVE] as const,
  series: (q: MetricSeriesQuery) => ['analytics', 'series', q, LIVE] as const,
};

export const analyticsApi = {
  dictionary: (c: HttpClient, preset: MetricPresetKey, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/dictionary', undefined, { preset }), {
      schema: MetricDictionarySchema,
      ...opt(signal),
    }),
  money: (c: HttpClient, signal?: AbortSignal) =>
    c.request('/analytics/money-board', { schema: MoneyBoardSchema, ...opt(signal) }),
  board: (c: HttpClient, q: BoardQuery, signal?: AbortSignal) =>
    snapshotRequest(c, buildPath('/analytics/board', undefined, q as Query), BoardSchema, signal),
  compare: (c: HttpClient, q: CompareQuery, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/compare', undefined, q as Query), {
      schema: OfficeComparisonSchema,
      ...opt(signal),
    }),
  rows: (c: HttpClient, metricId: string, q: MetricRowsQuery, signal?: AbortSignal) =>
    c.request(
      buildPath(`/analytics/metrics/${encodeURIComponent(metricId)}/rows`, undefined, q as Query),
      {
        schema: MetricRowsSchema,
        ...opt(signal),
      },
    ),
  huddle: (c: HttpClient, q: HuddleQuery, signal?: AbortSignal) =>
    snapshotRequest(c, buildPath('/analytics/huddle', undefined, q as Query), HuddleSchema, signal),
  huddleSummary: (c: HttpClient, q: HuddleQuery, signal?: AbortSignal) =>
    snapshotRequest(
      c,
      buildPath('/analytics/huddle/summary', undefined, q as Query),
      HuddleSummarySchema,
      signal,
    ),
  providers: (c: HttpClient, q: Query, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/providers', undefined, q), {
      schema: ScorecardsSchema,
      ...opt(signal),
    }),
  finder: (c: HttpClient, q: FinderQuery, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/finder', undefined, q as Query), {
      schema: FinderResultSchema,
      ...opt(signal),
    }),
  finderTasks: (c: HttpClient, body: FinderTasksBody) =>
    c.request('/analytics/finder/tasks', {
      method: 'POST',
      body,
      schema: FinderTasksResultSchema,
    }),
  daySheet: (c: HttpClient, date: string, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/reports/day-sheet', undefined, { date }), {
      schema: DaySheetSchema,
      ...opt(signal),
    }),
  production: (c: HttpClient, q: ProductionReportQuery, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/reports/production', undefined, q as Query), {
      schema: ProductionReportSchema,
      ...opt(signal),
    }),
  aging: (c: HttpClient, asOf: string, signal?: AbortSignal, page = 1, pageSize = 100) =>
    c.request(buildPath('/analytics/reports/aging', undefined, { asOf, page, pageSize }), {
      schema: AgingReportSchema,
      ...opt(signal),
    }),
  referrals: (c: HttpClient, from: string, to: string, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/reports/referrals', undefined, { from, to }), {
      schema: ReferralReportSchema,
      ...opt(signal),
    }),
  outliers: (c: HttpClient, signal?: AbortSignal) =>
    c.request('/analytics/outliers', { schema: FeeOutliersSchema, ...opt(signal) }),
  ackOutlier: (c: HttpClient, procedureId: string, note?: string) =>
    c.request(`/analytics/outliers/${encodeURIComponent(procedureId)}/ack`, {
      method: 'POST',
      body: note ? { note } : {},
      schema: OutlierAckResultSchema,
    }),
  goals: (c: HttpClient, month: string, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/goals', undefined, { month }), {
      schema: GoalsSchema,
      ...opt(signal),
    }),
  putGoal: (c: HttpClient, body: GoalPut) =>
    c.request('/analytics/goals', { method: 'PUT', body, schema: OfficeGoalSchema }),
  goalsV2: (c: HttpClient, month: string, signal?: AbortSignal) =>
    snapshotRequest(
      c,
      buildPath('/analytics/goals/v2', undefined, { month: month || undefined }),
      GoalsV2Schema,
      signal,
    ),
  putTargets: (c: HttpClient, body: GoalTargetPut) =>
    c.request('/analytics/goals/targets', { method: 'PUT', body, schema: SavedSchema }),
  putSettings: (c: HttpClient, body: AnalyticsSettingPut) =>
    c.request('/analytics/goals/settings', { method: 'PUT', body, schema: SavedSchema }),
  renegotiation: (c: HttpClient, months: number, signal?: AbortSignal) =>
    c.request(
      buildPath('/analytics/reports/renegotiation', undefined, { months: String(months) }),
      {
        schema: RenegotiationPackSchema,
        ...opt(signal),
      },
    ),
  belowContract: (c: HttpClient, months: number, signal?: AbortSignal) =>
    c.request(
      buildPath('/analytics/reports/below-contract', undefined, { months: String(months) }),
      {
        schema: BelowContractSchema,
        ...opt(signal),
      },
    ),
  belowContractTasks: (c: HttpClient, body: { lineIds: string[]; months: number }) =>
    c.request('/analytics/reports/below-contract/tasks', {
      method: 'POST',
      body,
      schema: BelowContractTasksResultSchema,
    }),
  /** Goals filled from history (not saved until Save goals). */
  suggestGoals: (
    c: HttpClient,
    q: { month: string; basis: 'last_year' | 'last_3_months'; percent: number },
  ) =>
    c.request(
      buildPath('/analytics/goals/suggest', undefined, {
        month: q.month || undefined,
        basis: q.basis,
        percent: String(q.percent),
      }),
      { schema: GoalSuggestionsSchema },
    ),
  putAlertThresholds: (c: HttpClient, body: AlertThresholdsPut) =>
    c.request('/analytics/goals/alert-thresholds', { method: 'PUT', body, schema: SavedSchema }),
  scorecard: (c: HttpClient, q: KpiScorecardQuery, signal?: AbortSignal) =>
    snapshotRequest(
      c,
      buildPath('/analytics/scorecard', undefined, q as Query),
      KpiScorecardSchema,
      signal,
    ),
  taskResults: (c: HttpClient, from: string, to: string, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/reports/task-results', undefined, { from, to }), {
      schema: TaskResultsSchema,
      ...opt(signal),
    }),
  reportSubs: (c: HttpClient, signal?: AbortSignal) =>
    c.request('/analytics/report-subscriptions', {
      schema: ReportSubscriptionsSchema,
      ...opt(signal),
    }),
  putReportSubs: (c: HttpClient, body: ReportSubscriptions) =>
    c.request('/analytics/report-subscriptions', {
      method: 'PUT',
      body,
      schema: ReportSubscriptionsSchema,
    }),
  schedule: (c: HttpClient, month: string, signal?: AbortSignal) =>
    snapshotRequest(
      c,
      buildPath('/analytics/schedule-analysis', undefined, { month: month || undefined }),
      ScheduleAnalysisSchema,
      signal,
    ),
  pivot: (c: HttpClient, q: PivotQuery, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/pivot', undefined, q as Query), {
      schema: PivotResultSchema,
      ...opt(signal),
    }),
  series: (c: HttpClient, q: MetricSeriesQuery, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/series', undefined, q as Query), {
      schema: MetricSeriesSchema,
      ...opt(signal),
    }),
  finderSearch: (c: HttpClient, body: FinderSearchBody, signal?: AbortSignal) =>
    c.request('/analytics/finder/search', {
      method: 'POST',
      body,
      schema: FinderSearchResultSchema,
      ...opt(signal),
    }),
  finderSearchExport: (c: HttpClient, body: FinderSearchBody) =>
    c.request('/analytics/finder/search/export', {
      method: 'POST',
      body,
      schema: FinderSearchExportSchema,
    }),
  finderActions: (c: HttpClient, body: FinderActionBody) =>
    c.request('/analytics/finder/search/actions', {
      method: 'POST',
      body,
      schema: FinderActionResultSchema,
    }),
  finderFacets: (c: HttpClient, signal?: AbortSignal) =>
    c.request('/analytics/finder/facets', { schema: FinderFacetsSchema, ...opt(signal) }),
  finderSaved: (c: HttpClient, signal?: AbortSignal) =>
    c.request('/analytics/finder/saved', { schema: FinderSavedSearchesSchema, ...opt(signal) }),
  saveFinderSearch: (
    c: HttpClient,
    body: { name: string; filters: FinderFilters; shared: boolean },
  ) =>
    c.request('/analytics/finder/saved', {
      method: 'POST',
      body,
      schema: FinderSavedSearchSchema,
    }),
  deleteFinderSearch: (c: HttpClient, id: string) =>
    c.request(`/analytics/finder/saved/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      schema: z.object({ ok: z.literal(true) }),
    }),
  /** Money you can recover (roadmap Tier 1 #1-6); a stored result (202 while it is prepared). */
  recoverable: (c: HttpClient, signal?: AbortSignal) =>
    snapshotRequest(c, '/analytics/recoverable', RecoverableMoneySchema, signal),
  unscheduledAging: (c: HttpClient, asOf: string, signal?: AbortSignal) =>
    c.request(buildPath('/analytics/reports/unscheduled-aging', undefined, { asOf }), {
      schema: UnscheduledAgingSchema,
      ...opt(signal),
    }),
};

/**
 * Same-origin URL of the whole Finder segment as CSV (HA-10): the server
 * builds every row, not just the 2,000 on screen. Cookie auth; the office
 * selection travels as the `office` query parameter.
 */
export function finderExportUrl(q: FinderQuery, office: string): string {
  return buildPath('/v1/analytics/finder/export', undefined, { ...(q as Query), office });
}

/** Same-origin URL of a pivot as CSV (every group; cookie auth, office as a parameter). */
export function pivotExportUrl(q: PivotQuery, office: string): string {
  return buildPath('/v1/analytics/pivot/export', undefined, { ...(q as Query), office });
}
